/* eslint-disable consistent-return */

import { Mongo } from 'meteor/mongo';
import SimpleSchema from 'simpl-schema';
import faker from 'faker';
import { map, includes, find, isNil, get } from 'lodash';

import requiredIf from '../../modules/schema-conditional-required';
import { GRAPH_ELEMENTS, ENTRY_DATA_TYPES } from '../../modules/graphic-elements';

export const GRAPH_STYLE_TYPES = {
  SINGLE: 'single',
  RANGED: 'ranged',
  CUSTOM: 'custom',
  properties: {
    SINGLE: {
      name: 'SINGLE',
      value: 'single',
      humanValue: 'Fixed Color'
    },
    RANGED: {
      name: 'RANGED',
      value: 'ranged',
      humanValue: 'Color Range'
    },
    CUSTOM: {
      name: 'CUSTOM',
      value: 'custom',
      humanValue: 'Custom'
    }
  }
};

/**
 * A graphic object for ipsilum maps.
 *
 * @typedef {object} Graphic
 * @property {string} _id - The graphic id.
 * @property {string} owner - The owner id.
 * @property {string} createdAt - The date when it was created.
 * @property {string} updatedAt - The date when it was last updated.
 * @property {string} deleted - Indicates whether the graphic is in the trash or not.
 * @property {string} name - The name for the graphic.
 * @property {string} layerId - The id of the layer the graphic belongs.
 * @property {string} graphicType - The graphic type. One of [PIECHART,DONUT,CIRCULARBARCHART,BARCHART,HORIZONTALBAR,LINECHART,AREACHART].
 * @property {numericProperty[]} numericProperties - The numeric properties to create the graphic.
 * @property {globalProperty[]} globalProperties - The global properties to create the graphic.
 * @property {string} namedProperty - The named properties to create the graphic.
 * @property {selectedElementsData} selectedElements - The elements to be shown with the graphic.
 * @property {boolean} shared - Indicates whether it is being shared or not.
 * @property {boolean} sharedPrivate - Indicates whether it is being shared or not.
 * @property {string} publicId - The public key generated for the URL.
 */

/**
 * The object that stores the elements to be shown with the graphic.
 *
 * @typedef {object} selectedElementsData
 * @property {boolean} Legend - Show or hide the legend.
 * @property {boolean} Text - Show or hide the text.
 * @property {boolean} Axis - Show or hide the axis.
 */

/**
 * The object that stores numeric properties and their style.
 *
 * @typedef {object} numericProperty
 * @property {string} property - The name of the property.
 * @property {string} styleType - Simbology type for the style. One of [single,ranged,custom].
 * @property {styleData} styleData - The style defined.
 */

/**
 * The object that stores global properties and their values.
 *
 * @typedef {object} globalProperty
 * @property {string} name - The name of the property.
 * @property {string} value - The value of the property.
 */

/**
 * The object that stores the user defined style.
 *
 * @typedef {object} styleData
 * @property {string} color - The color for the style.
 * @property {string} colorRange - The color range for the style.
 * @property {number} alpha - The trasparency for the style.
 * @property {number} weight - The weight of the line for the style.
 * @property {number} borderAlpha - The trasparency of the outline for the style.
 * @property {string} borderColor -The color of the outline in hexadecimal.
 * @property {legendData[]} legend - The legend for the ranged and custom styles.
 */

/**
 * The legend for the ranged and custom styles.
 *
 * @typedef {object} legendData
 * @property {string} value - The value of the property in the Feature or the range.
 * @property {string} color - The color for this value or range.
 * @property {number} count - The number of times this value appears in the geojson.
 * @property {number} index - An integer to sort the values.
 */

const Graphics = new Mongo.Collection('Graphics');

Graphics.allow({
  insert: () => false,
  update: () => false,
  remove: () => false
});

Graphics.deny({
  insert: () => true,
  update: () => true,
  remove: () => true
});

const selectedElementsDataSchema = new SimpleSchema({
  Text: {
    type: Boolean,
    label: 'Flag that checks if the text is shown'
  },
  Axis: {
    type: Boolean,
    label: 'Flag that checks if the axis are shown'
  },
  Legend: {
    type: Boolean,
    label: 'Flag that checks if the legend is shown'
  }
});

const legendSchema = new SimpleSchema({
  value: {
    type: SimpleSchema.oneOf(String, Number),
    label: 'The value of the property in the Feature'
  },
  color: {
    type: String,
    label: 'The color for this value'
  },
  count: {
    type: Number,
    label: 'The number of times this value appears in the geojson'
  },
  index: {
    type: Number,
    label: 'An integer to sort the values'
  }
});

const styleDataSchema = new SimpleSchema({
  color: {
    type: String,
    label: 'The color in hexadecimal',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: this.field('styleType').value === GRAPH_STYLE_TYPES.SINGLE,
        schemaInstance: this
      });
    }
  },
  colorRange: {
    type: String,
    label: 'The name of the color range',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes(
          [GRAPH_STYLE_TYPES.RANGED, GRAPH_STYLE_TYPES.CUSTOM],
          this.field('styleType').value
        ),
        schemaInstance: this
      });
    }
  },
  alpha: {
    type: Number,
    label: 'The trasparency for the style',
    min: 0,
    max: 1
  },
  weight: {
    type: Number,
    label: 'The weight of the line for the style',
    min: 0,
    max: 100
  },
  borderAlpha: {
    type: Number,
    label: 'The trasparency of the outline for the style',
    min: 0,
    max: 1
  },
  borderColor: {
    type: String,
    label: 'The color of the outline in hexadecimal'
  },
  legend: {
    type: Array,
    label: 'The legend for the numeric and categorized styles',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes(
          [GRAPH_STYLE_TYPES.RANGED, GRAPH_STYLE_TYPES.CUSTOM],
          this.field('styleType').value
        ),
        schemaInstance: this
      });
    }
  },
  'legend.$': {
    type: legendSchema,
    label: 'The legend for each value in the style',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes(
          [GRAPH_STYLE_TYPES.RANGED, GRAPH_STYLE_TYPES.CUSTOM],
          this.field('styleType').value
        ),
        schemaInstance: this
      });
    }
  }
});

const numericPropertySchema = new SimpleSchema({
  property: {
    type: String,
    label: 'The name of the property'
  },
  styleType: {
    type: String,
    label: 'The type of the style for the graphic',
    allowedValues: map(GRAPH_STYLE_TYPES.properties, 'value')
  },
  styleData: {
    type: styleDataSchema,
    label: 'The style for this property'
  }
});

const globalPropertySchema = new SimpleSchema({
  name: {
    type: String,
    label: 'The name of the property'
  },
  value: {
    type: String,
    label: 'The value of the property'
  }
});

Graphics.schema = new SimpleSchema({
  owner: {
    type: String,
    label: 'The ID of the user this graphic belongs to'
  },
  createdAt: {
    type: String,
    label: 'The date this graphic was created in the database',
    autoValue() {
      if (this.isInsert) return new Date().toISOString();
    }
  },
  updatedAt: {
    type: String,
    label: 'The date this graphic was last updated',
    autoValue() {
      if (this.isInsert || this.isUpdate) return new Date().toISOString();
    }
  },
  name: {
    type: String,
    label: 'A name to identify the graphic'
  },
  layerId: {
    type: String,
    label: 'The ID of the layer the graphic belongs to'
  },
  graphicType: {
    type: String,
    label: 'The type of the graphic added',
    allowedValues: map(GRAPH_ELEMENTS.properties, 'name')
  },
  numericProperties: {
    type: Array,
    label: 'The numeric properties to create the graphic'
  },
  'numericProperties.$': {
    type: numericPropertySchema,
    label: 'The object with the name and the style of the property'
  },
  globalProperties: {
    type: Array,
    label: 'The global properties to create the graphic',
    optional: true
  },
  'globalProperties.$': {
    type: globalPropertySchema,
    label: 'An object with the name and the value of the property'
  },
  namedProperty: {
    type: String,
    label: 'The named property to create the graphic',
    optional: true,
    custom() {
      const graphElement = find(GRAPH_ELEMENTS.properties, [
        'name',
        this.field('graphicType').value
      ]);
      return requiredIf({
        requiredCondition: graphElement.dataNamedType !== ENTRY_DATA_TYPES.NONE,
        schemaInstance: this
      });
    }
  },
  selectedElements: {
    type: selectedElementsDataSchema,
    label: 'The elements to be shown with the graphic'
  },
  shared: {
    type: Boolean,
    label: 'Flag that checks if graphic is shared',
    autoValue() {
      if (this.isInsert) return false;
    }
  },
  sharedPrivate: {
    type: Boolean,
    label: 'Flag that checks if graphic is shared',
    autoValue() {
      if (this.isInsert) return false;
    }
  },
  publicId: {
    type: String,
    label: 'The public key generated for the URL',
    optional: true,
    autoValue() {
      const publicId = get(Graphics.findOne(this.docId), 'publicId');
      if (this.isInsert || (this.isUpdate && isNil(publicId))) return faker.internet.password();
    }
  },
  deleted: {
    type: String,
    label: 'The date the graphic was deleted',
    autoValue() {
      if (this.isInsert) return 'no';
    }
  }
});

Graphics.attachSchema(Graphics.schema);

export default Graphics;
